import React, { useEffect, useState, lazy, Suspense, useRef } from "react";
import jwt from "jsonwebtoken";
import { connect } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";

import { setDrawerOpenGlobal, setPayment } from "../../store/actions/account";
import { getProjectDetails } from "../../store/actions/project";
import API from "../../utils/axios";
import VehicleSettingsSetup from "../common/VehicleSettingsSetup";
import { getCookie } from "../../utils/cookies";
import history from "../../utils/history";
import AccountDrawer from "../accountDrawer/AccountDrawer";

import InfoDialog from "../dialogs/InfoDialog";
import {
  LANDING_URL,
  paths,
  COMPANY_TYPE_PREDEFINED_VALUES
} from "../data/constants";
import Drawer from "../drawer/Drawer";
import MapWrapper from "../map/Map";
import PageProjects from "./pages/projects/PageProjects";
import mixpanelWrapper from "../../utils/mixpanel";
import { capitalize } from "../../utils/helpers";
import * as Sentry from "@sentry/react";
import TourProvider from "../../states/tour";
import FirebaseProvider from "../../states/firebase";
import MapOl from "../map-ol";
const StopStats = lazy(() => import("../project/StopStats"));

const useStyles = makeStyles(theme => ({
  pagesWrapper: {
    height: "100vh",
    maxHeight: "100vh",
    overflow: "auto",
    width: ({ accountDrawerOpen, showProjects }) =>
      !showProjects
        ? "100%"
        : accountDrawerOpen
        ? "calc(100% - 280px)"
        : "calc(100% - 56px)",
    marginLeft: ({ accountDrawerOpen, showProjects }) =>
      !showProjects ? 0 : accountDrawerOpen ? 280 : 56,
    transition: ({ accountDrawerOpen, showProjects }) =>
      !showProjects
        ? "none"
        : theme.transitions.create(["width", "margin-left"], {
            duration: accountDrawerOpen
              ? theme.transitions.duration.leavingScreen
              : theme.transitions.duration.enteringScreen
          })
  }
}));

const Dashboard = ({
  projectId,
  getProjectDetails,
  userType,
  userData,
  userLoaded,
  accountDrawerOpen,
  setDrawerOpenGlobal,
  projectsDisabled,
  setPayment,
  isProjectPublished,
  vehiclesCount
}) => {
  // States
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [vehicleSettingsSetupStatus, setVehicleSettingsSetupStatus] = useState({
    loaded: false,
    completed: false
  });

  // General
  const match = useRouteMatch("/:projectId");

  // Variables
  const userId = userData.id;
  // Project id from route
  const projectIdUrl =
    match &&
    (paths.includes(match.params.projectId)
      ? sessionStorage.currentProject
      : match.params.projectId);

  const organisation =
    userData.organisationSite && userData.organisationSite.organisation;

  const expired =
    organisation &&
    ((organisation.subscription_status === "trialing" &&
      new Date() > new Date(organisation.trial_end * 1000)) ||
      organisation.subscription_status === "canceled");
  const incompleteSubscriptionStatus =
    organisation?.subscription_status === "incomplete";
  const incompleteAccount =
    organisation &&
    organisation.subscription_last_payment_status ===
      "requires_payment_method" &&
    incompleteSubscriptionStatus;
  const incompletePayment =
    organisation &&
    organisation.subscription_last_payment_status === "requires_action" &&
    incompleteSubscriptionStatus;
  const failedToProcess =
    organisation?.subscription_status === "canceled" &&
    organisation.subscription_last_payment_status === "requires_payment_method";
  const failedToProcessTrial =
    organisation?.subscription_status === "trialing" &&
    organisation.subscription_last_payment_status === "requires_payment_method";

  // Effects
  useEffect(() => {
    if (!projectIdUrl) {
      setDrawerVisible(false);
    } else {
      setDrawerVisible(true);
    }
  }, [projectIdUrl]);

  const [gotDetails, setGotDetails] = useState(false);

  // Update project
  useEffect(() => {
    if (!gotDetails && userType && userLoaded) {
      let token = getCookie("accessToken");
      const expiresAt = getCookie("expiresAt");

      if (token && new Date(+expiresAt || expiresAt) > new Date()) {
        token = token.slice(7);
        const decoded = jwt.decode(token, { complete: true });

        if (decoded) {
          // Shared properties
          const humanReadablePlanName =
            userData.organisationSite.organisation.plan.type
              .split("_")
              .map(capitalize)
              .join(" ");
          const planName =
            userData.organisationSite.organisation.subscription_status ===
            "trialing"
              ? `${humanReadablePlanName}(Trial)`
              : `${humanReadablePlanName}`;

          // Track FullStory
          if (window.FS) {
            window.FS.setUserVars({
              displayName: `${userData.firstName} ${userData.lastName}`,
              email: userData.email,
              subscriptionType_str:
                userData.organisationSite.organisation.plan.type, // Routetitan plan
              rtEnvironment_str: process.env.REACT_APP_ENVIRONMENT, // Routetitan environment
              rtOrganisationId_str: userData.organisationSite.organisation.id // Routetitan organisation id
            });
          }

          // Track PostHog
          if (window.posthog) {
            window.posthog.identify(`${userData.email}`, {
              email: userData.email,
              accountId: userData.id,
              organisationId: userData.organisationSite.organisation.id,
              fullName: `${userData.firstName} ${userData.lastName}`,
              organisationName: userData.organisationSite.organisation.name,
              subscriptionName: userData.organisationSite.organisation.plan.type
            });
          }

          // Track Mixpanel
          // const humanReadablePlanName =
          //   userData.organisationSite.organisation.plan.type
          //     .split("_")
          //     .map(capitalize)
          //     .join(" ");
          // const planName =
          //   userData.organisationSite.organisation.subscription_status ===
          //   "trialing"
          //     ? `${humanReadablePlanName}(Trial)`
          //     : `${humanReadablePlanName}`;
          mixpanelWrapper.identify(userId);
          // Register super properties
          mixpanelWrapper.register({ "Plan Name": planName });
          mixpanelWrapper.people.set({
            $first_name: userData.firstName,
            $last_name: userData.lastName,
            $email: userData.email,
            "Full Name": userData.fullName,
            "Company Name": userData.organisationSite.organisation.name,
            "Plan Name": planName,
            "Company Type": COMPANY_TYPE_PREDEFINED_VALUES.includes(
              userData.organisationSite.organisation.industry
            )
              ? userData.organisationSite.organisation.industry
              : "Other",
            ...(!COMPANY_TYPE_PREDEFINED_VALUES.includes(
              userData.organisationSite.organisation.industry
            )
              ? {
                  "Company Type Other Description":
                    userData.organisationSite.organisation.industry
                }
              : {}),
            "User Role": userData.organisationSite.organisation.user_role,
            "Current Route Planner":
              userData.organisationSite.organisation.route_planner,
            "Hear about us":
              userData.organisationSite.organisation.hear_about_us,
            "Usage Type": userData.organisationSite.organisation.usage_type
          });

          // Sentry
          Sentry.setUser({ id: userData.id, email: userData.email });

          // Google Tag Manager Data Layer
          window.dataLayer.push({
            event: "login",
            userId: userData.id
          });
        }

        API.get("/v1/settings/vehicles")
          .then(() => {
            setVehicleSettingsSetupStatus({
              loaded: true,
              completed: true
            });
          })
          .catch(error => {
            if (error.status === 404) {
              setVehicleSettingsSetupStatus({
                loaded: true,
                completed: false
              });
            } else {
              console.log(error);
            }
          });

        API.get("v1/stripe/payments").then(res => {
          const payment = res.data.find(thePayment => thePayment.default_pm);
          if (payment) {
            setPayment(payment);
          }
        });
        setGotDetails(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, getProjectDetails, projectIdUrl, userLoaded]);

  const [gotAccountDetails, setGotAccountDetails] = useState(false);

  useEffect(() => {
    // Choose the right id and fetch data
    if (gotDetails && userLoaded) {
      if (projectIdUrl && projectIdUrl !== projectId) {
        setGotAccountDetails(true);
        sessionStorage.currentProject = projectIdUrl;
        getProjectDetails(projectIdUrl, true);
      } else if (!projectIdUrl && !gotAccountDetails) {
        sessionStorage.currentProject = "";
        getProjectDetails();
        setGotAccountDetails(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdUrl, gotDetails, userLoaded]);

  useEffect(() => {
    if (userLoaded) {
      if (!userType) {
        if (process.env.NODE_ENV === "development") {
          history.push("/login");
        } else {
          window.location.href = `${LANDING_URL}/login`;
        }
      }
    }
  }, [userLoaded, userType]);

  useEffect(() => {
    console.log(match);
    if (!match) {
      // Include the query params in the redirect
      history.push(`/projects${window.location.search}`);
    }

    const shouldToShowMap =
      drawerVisible &&
      !projectsDisabled &&
      match?.params?.projectId !== "projects";

    shouldToShowMap ? setShowMap(true) : setShowMap(false);
  }, [drawerVisible, match, projectsDisabled]);

  useEffect(() => {
    if (
      match &&
      match.params &&
      match.params.projectId === "projects" &&
      sessionStorage.currentProject
    ) {
      sessionStorage.currentProject = "";
    }
  }, [match]);

  const showProjects = !(drawerVisible && sessionStorage.currentProject);
  const classes = useStyles({ accountDrawerOpen, showProjects });

  return (
    <Box display="flex">
      {userLoaded && userType && vehicleSettingsSetupStatus.loaded && (
        <>
          <FirebaseProvider>
            <TourProvider>
              <div className={classes.pagesWrapper} id="pages_wrapper">
                <AccountDrawer
                  visible={showProjects}
                  expanded={!drawerVisible}
                  open={accountDrawerOpen}
                  setOpen={setDrawerOpenGlobal}
                  saveDisabled={saveDisabled}
                  setSaveDisabled={setSaveDisabled}
                  organisation={organisation}
                  expired={expired}
                  incompleteSubscriptionStatus={incompleteSubscriptionStatus}
                  incompleteAccount={incompleteAccount}
                  incompletePayment={incompletePayment}
                  failedToProcess={failedToProcess}
                  failedToProcessTrial={failedToProcessTrial}
                  vehicleSettingsSetupStatus={vehicleSettingsSetupStatus}
                />
                <VehicleSettingsSetup
                  vehicleSettingsSetupStatus={vehicleSettingsSetupStatus}
                  setVehicleSettingsSetupStatus={setVehicleSettingsSetupStatus}
                />
                <InfoDialog />
                <div
                  style={{
                    visibility: showMap && !showProjects ? "visible" : "hidden"
                  }}
                >
                  {true ? <MapOl /> : <MapWrapper />}
                </div>
                {showProjects && (
                  <PageProjects
                    saveDisabled={saveDisabled}
                    setSaveDisabled={setSaveDisabled}
                    expired={expired}
                    incompleteAccount={incompleteAccount}
                    incompletePayment={incompletePayment}
                    failedToProcess={failedToProcess}
                    failedToProcessTrial={failedToProcessTrial}
                    setAccountDrawerOpen={setDrawerOpenGlobal}
                  />
                )}
                {!showProjects && drawerVisible && !projectsDisabled && (
                  <>
                    <Drawer />
                    {isProjectPublished && (
                      <Suspense fallback={<h1>Loading...</h1>}>
                        <StopStats />
                      </Suspense>
                    )}
                  </>
                )}
              </div>
            </TourProvider>
          </FirebaseProvider>
        </>
      )}
    </Box>
  );
};

function mapStateToProps(state) {
  return {
    projectId: state.project.id,
    // pinpointDriver: state.map.pinpointDriver,
    accountDrawerOpen: state.account.accountDrawerOpen,
    userType: state.auth.userData.userType,
    userLoaded: state.auth.userLoaded,
    userData: state.auth.userData,
    projectsDisabled: state.common.projectsDisabled,
    isProjectPublished: state.project.published || state.project.published_once,
    vehiclesCount: state.project.vehicleTypes.length
  };
}

export default connect(mapStateToProps, {
  getProjectDetails,
  setDrawerOpenGlobal,
  setPayment
})(Dashboard);
